import type { Replace } from '@weft/utils';
import { isNotNullOrUndefined } from '@weft/utils';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import type { ForwardedRef } from 'react';
import type { FieldValues, FieldPath, FieldError, Merge, FieldErrorsImpl } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { EditIcon } from '../../icons';
import { TrashIcon } from '../../icons/TrashIcon';
import { ErrorLabel } from '../ErrorLabel';
import { DEBOUNCE_DELAY } from './input-consts';

export type HTMLFormInputElement<ValidatedObjectSchema extends FieldValues> = Replace<HTMLInputElement, 'name', FieldPath<ValidatedObjectSchema>>;

export type InputProps<ValidatedObjectSchema extends FieldValues> =
  Partial<Omit<Replace<React.InputHTMLAttributes<HTMLFormInputElement<ValidatedObjectSchema>>, 'onChange', React.ChangeEventHandler<HTMLFormInputElement<ValidatedObjectSchema>>>, 'ref' | 'prefix'>>
  & {
    inputRef?: React.RefObject<HTMLInputElement>
    selectOnFocus?: boolean;
    label?: string;
    labelProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLLabelElement>, HTMLLabelElement>;
    errorMessage?: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
    showErrorMessage?: boolean;
    editMode?: boolean;
    onEditModeChange?: (isInEditMode: boolean) => void;
    editIconClassName?: string;
    wrapperClassName?: string;
    prefix?: React.ReactNode;
    prefixClassName?: string;
    suffix?: React.ReactNode;
    suffixWrapperClassName?: string;
    debounce?: boolean;
    swallowClicks?: boolean;
    showEditIcon?: boolean;
    showTrashIcon?: boolean;
    onTrashIconClick?: () => void;
    isInEditModeOverride?: boolean;
    editModeOverride?: boolean;
  };

let debounceTimeout: NodeJS.Timeout | undefined;

export const Input = forwardRef(
  <ValidatedObjectSchema extends FieldValues>(
    {
      wrapperClassName,
      className: externalClassName,
      selectOnFocus,
      onFocus: externalOnFocus,
      label,
      labelProps,
      errorMessage,
      showErrorMessage = true,
      editMode = false,
      editIconClassName,
      prefix,
      prefixClassName,
      suffix,
      suffixWrapperClassName,
      debounce = true,
      onEditModeChange,
      swallowClicks = true,
      showEditIcon,
      showTrashIcon,
      onTrashIconClick,
      editModeOverride = false,
      isInEditModeOverride = false,
      ...props
    }: InputProps<ValidatedObjectSchema>,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const [isInEditMode, setIsInEditMode] = useState(false);
    const hasError = isNotNullOrUndefined(errorMessage);

    const inputClassName = twMerge(
      'outline-none bg-input w-full text-ellipsis rounded-2lg placeholder:text-placeholder text-filled-text bg-clip-content py-3 bg-clip-border px-6.5',
      editMode || isInEditModeOverride ? 'duration-200 linear' : '',
      (!editMode || isInEditMode) || isInEditModeOverride ? 'cursor-text' : `${!editModeOverride ? 'cursor-pointer' : 'cursor-default opacity-50'} bg-transparent p-0`,
      hasError && !showEditIcon && isInEditMode ? 'border border-input-error' : '',
      isNotNullOrUndefined(prefix) ? 'pl-8' : '',
      isNotNullOrUndefined(suffix) ? 'pr-16' : '',
      externalClassName,
    );

    const wrapperInputClassName = twMerge(
      'outline-none bg-input w-full rounded-2lg',
      editMode ? 'duration-200 linear' : '',
      (!editMode || isInEditMode) || isInEditModeOverride ? 'cursor-text' : `${!editModeOverride ? 'cursor-pointer' : 'cursor-default'} bg-transparent p-0`,
      hasError && !showEditIcon && isInEditMode ? 'border border-input-error' : '',
    );

    const swallowClick = (e: React.MouseEvent) => {
      if (swallowClicks) {
        e.stopPropagation();
      }
    };

    const handleOutsideClick = (e: MouseEvent) => {
      if (e.target === e.currentTarget) return;

      handleEditModeChange(false)();
    };

    useEffect(() => {
      if (isInEditMode) document.addEventListener('click', handleOutsideClick);

      return () => document.removeEventListener('click', handleOutsideClick);
    }, [isInEditMode]);

    const onChange = (event: React.ChangeEvent<HTMLFormInputElement<ValidatedObjectSchema>>) => {
      clearTimeout(debounceTimeout);

      if (debounce) {
        debounceTimeout = setTimeout(() => props.onChange?.(event), DEBOUNCE_DELAY);
      } else {
        props.onChange?.(event);
      }
    };

    const onFocus = useCallback((event: React.FocusEvent<HTMLFormInputElement<ValidatedObjectSchema>>) => {
      if (selectOnFocus) {
        event.target.select();
      }

      externalOnFocus?.(event);
    }, [selectOnFocus]);

    const handleEditModeChange = useCallback((newEditMode: boolean) => () => {
      if (!editModeOverride) {
        setIsInEditMode(newEditMode);
        onEditModeChange?.(newEditMode);
      }
    }, [editModeOverride, onEditModeChange]);

    return (
      <div className={twMerge('flex w-full flex-col', wrapperClassName)}>
        {
          isNotNullOrUndefined(label) && (
            <label
              className={twMerge('mb-2 text-sm text-label')}
              htmlFor={props.id}
              {...labelProps}
            >
              {label}
            </label>
          )
        }

        <div
          className={twMerge('group relative flex full items-center justify-start', wrapperInputClassName, wrapperClassName)}
          onClick={swallowClick}
        >
          {
            isNotNullOrUndefined(prefix) && (
              <span
                className={twMerge('text-placeholder absolute pl-4 text-base pointer-events-none', prefixClassName)}
                onClick={e => e.preventDefault()}
              >
                {prefix}
              </span>
            )
          }

          <input
            className={inputClassName}
            onClick={handleEditModeChange(editMode)}
            readOnly={(editMode && !isInEditMode) && !isInEditModeOverride}
            onFocus={onFocus}
            {...props}
            onChange={onChange}
            ref={ref}
            onWheel={(event) => (event.target as HTMLElement).blur()}
          />

          {
            isNotNullOrUndefined(suffix) && (
              <span className={twMerge('text-placeholder absolute right-4 text-base', suffixWrapperClassName)}>
                {suffix}
              </span>
            )
          }
          <div className='flex items-center gap-2'>
            {(editMode && !isInEditMode) && !editModeOverride && (
              <EditIcon
                onClick={handleEditModeChange(editMode)}
                className={twMerge('ml-1 min-h-[1.25rem] min-w-[1.25rem] bg-white opacity-0 duration-200 ease-in', editIconClassName, showEditIcon ? 'opacity-100' : 'group-hover:opacity-100')}
              />
            )}
            {(showTrashIcon && !isInEditMode) && !editModeOverride && (
              <button 
                type='button'
                onClick={onTrashIconClick}
                className='flex items-center'
              >
                <TrashIcon 
                  className='fill-icon mb-0.25'
                />
              </button>
            )}
          </div>

        </div>
        {
          showErrorMessage && hasError && (<ErrorLabel errorMessage={errorMessage}/>)
        }
      </div>
    );
  });